import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { PendingReview } from '../types/review';

interface ReviewsContextType {
    reviews: PendingReview[];
    addReview: (review: Omit<PendingReview, 'id' | 'status'>) => string; // Returns reviewId
    approveReview: (id: string, response: string) => void;
    rejectReview: (id: string) => void;
    requestClarification: (id: string, clarification: string) => void;
}

const ReviewsContext = createContext<ReviewsContextType | undefined>(undefined);

export const useReviews = () => {
    const context = useContext(ReviewsContext);
    if (!context) {
        throw new Error('useReviews must be used within ReviewsProvider');
    }
    return context;
};

interface ReviewsProviderProps {
    children: ReactNode;
}

export const ReviewsProvider: React.FC<ReviewsProviderProps> = ({ children }) => {
    const [reviews, setReviews] = useState<PendingReview[]>([
        // Mock data for demo
        {
            id: 'review-1',
            question: "@raza, what would it take we can hit our api targets on Q3 instead of Q4?",
            askedBy: 'Jack',
            askedAt: new Date(Date.now() - 1000 * 60 * 30), // 30 minutes ago
            mentionedPerson: 'Raza',
            context: 'Previous conversation about Q4 API targets and timeline adjustments',
            status: 'pending',
        },
        {
            id: 'review-2',
            question: "How can we accelerate the deployment timeline for the new feature?",
            askedBy: 'Sajid',
            askedAt: new Date(Date.now() - 1000 * 60 * 60), // 1 hour ago
            context: 'Discussion about feature deployment and timeline constraints',
            status: 'pending',
        },
    ]);

    const addReview = useCallback((review: Omit<PendingReview, 'id' | 'status'>): string => {
        const reviewId = `review-${Date.now()}`;
        const newReview: PendingReview = {
            ...review,
            id: reviewId,
            status: 'pending',
        };
        setReviews(prev => [newReview, ...prev]);
        return reviewId;
    }, []);

    const approveReview = useCallback((id: string, response: string) => {
        setReviews(prev =>
            prev.map(review =>
                review.id === id
                    ? { ...review, status: 'approved' as const, response }
                    : review
            )
        );
    }, []);

    const rejectReview = useCallback((id: string) => {
        setReviews(prev =>
            prev.map(review =>
                review.id === id ? { ...review, status: 'rejected' as const } : review
            )
        );
    }, []);

    const requestClarification = useCallback((id: string, clarification: string) => {
        setReviews(prev =>
            prev.map(review =>
                review.id === id
                    ? {
                        ...review,
                        status: 'clarification-requested' as const,
                        clarificationRequest: clarification,
                    }
                    : review
            )
        );
    }, []);

    return (
        <ReviewsContext.Provider
            value={{
                reviews,
                addReview,
                approveReview,
                rejectReview,
                requestClarification,
            }}
        >
            {children}
        </ReviewsContext.Provider>
    );
};

